import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    useReactTable,
    getCoreRowModel,
    getSortedRowModel,
    getPaginationRowModel,
    flexRender,
    type ColumnDef,
    type SortingState,
} from '@tanstack/react-table';
import { Trash2, AlertTriangle, RefreshCw } from 'lucide-react';
import styled from 'styled-components';
import type { ActiveJob, DatasetListItem } from '../types';
import { OverflowTitle } from './OverflowTitle';
import { colors, radii, transitions } from '../theme/tokens';
import { getToneBackground, getToneForeground } from '../theme/tone';
import { formatNumber, formatTimestamp } from '../utils/format';
import { ProgressBar } from './shared/ProgressBar';
import { TableWrapper, StyledTable, Thead, Tbody, Th, Tr, Td, SortIndicator, EmptyRow } from './shared/Table';
import { SearchFilterBar } from './shared/SearchFilterBar';
import { TablePagination } from './shared/TablePagination';

interface DatasetTableProps {
    datasets: DatasetListItem[];
    selectedDatasetPath?: string | null;
    onOpen: (dataset: DatasetListItem) => void;
    onDelete?: (dataset: DatasetListItem) => void;
    isDeleting?: boolean;
    deletingDatasetPath?: string | null;
    activeJobByDataset?: Map<string, ActiveJob>;
}

interface DatasetRow {
    id: string;
    displayTitle: string;
    documentCount: number;
    pageCount: number;
    reviewCompletion: number;
    updatedLabel: string;
    lastModified: number;
    warningsCount: number;
    dataset: DatasetListItem;
    activeJob: ActiveJob | null;
}

export const DeleteButton = styled.button<{ $floating?: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 6px;
    min-width: 32px;
    min-height: 32px;
    border: none;
    border-radius: ${radii.pill};
    background: transparent;
    color: ${colors.status.danger};
    cursor: pointer;
    transition: color ${transitions.default}, background ${transitions.default};

    &:hover {
        background: rgba(248, 113, 113, 0.12);
    }

    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }

    &:disabled:hover {
        background: transparent;
    }

    &:focus-visible {
        outline: 2px solid rgba(248, 113, 113, 0.45);
        outline-offset: 2px;
    }

    ${({ $floating }) =>
        $floating &&
        `
      position: absolute;
      top: 10px;
      right: 10px;
    `};
`;

const WarningPill = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    border-radius: ${radii.pill};
    padding: 4px 8px;
    background: ${getToneBackground('warning')};
    color: ${getToneForeground('warning')};
`;

const ProcessingPill = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    border-radius: ${radii.pill};
    padding: 4px 8px;
    background: ${getToneBackground('info')};
    color: ${getToneForeground('info')};

    & > svg {
        animation: spin 1.2s linear infinite;
    }

    @keyframes spin {
        from {
            transform: rotate(0deg);
        }
        to {
            transform: rotate(360deg);
        }
    }
`;

const ProgressCell = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
`;

const ProgressLabel = styled.span`
    font-size: 12px;
    color: #999;
    font-variant-numeric: tabular-nums;
    min-width: 32px;
    text-align: right;
`;

const getContainingDirectory = (metadataPath: string): string | null => {
    if (!metadataPath) {
        return null;
    }
    const normalized = metadataPath.replace(/\\+/g, '/');
    const segments = normalized.split('/').filter(Boolean);
    if (segments.length <= 1) {
        return null;
    }
    const name = segments[segments.length - 2] ?? null;
    return name ? name.replace(/_dataset$/i, '') : null;
};

const toDatasetRows = (datasets: DatasetListItem[], activeJobByDataset?: Map<string, ActiveJob>): DatasetRow[] =>
    datasets
        .filter(dataset => !!dataset.metadataPath)
        .map(dataset => {
            const reviewCompletion = dataset.reviewCompletion ?? 0;
            const displayTitle = getContainingDirectory(dataset.metadataPath) ?? dataset.title;
            const activeJob =
                activeJobByDataset?.get(dataset.metadataPath) ?? activeJobByDataset?.get(dataset.datasetId) ?? null;
            return {
                id: dataset.metadataPath,
                dataset,
                displayTitle,
                documentCount: dataset.documentCount,
                pageCount: dataset.pageCount,
                reviewCompletion,
                updatedLabel: formatTimestamp(dataset.lastModified),
                lastModified: dataset.lastModified,
                warningsCount: dataset.warnings.length,
                activeJob,
            };
        });

export const DatasetTable: React.FC<DatasetTableProps> = ({
    datasets,
    selectedDatasetPath,
    onOpen,
    onDelete,
    isDeleting,
    deletingDatasetPath,
    activeJobByDataset,
}) => {
    const onDeleteRef = useRef(onDelete);
    const isDeletingRef = useRef(isDeleting);
    const deletingDatasetPathRef = useRef(deletingDatasetPath);

    useEffect(() => {
        onDeleteRef.current = onDelete;
    }, [onDelete]);
    useEffect(() => {
        isDeletingRef.current = isDeleting;
    }, [isDeleting]);
    useEffect(() => {
        deletingDatasetPathRef.current = deletingDatasetPath;
    }, [deletingDatasetPath]);

    const [sorting, setSorting] = useState<SortingState>([{ id: 'lastModified', desc: true }]);
    const [searchQuery, setSearchQuery] = useState('');

    const rows = useMemo(() => toDatasetRows(datasets, activeJobByDataset), [datasets, activeJobByDataset]);

    const filteredRows = useMemo(() => {
        if (!searchQuery) return rows;
        const q = searchQuery.toLowerCase();
        return rows.filter(r => r.displayTitle.toLowerCase().includes(q));
    }, [rows, searchQuery]);

    const columns = useMemo<ColumnDef<DatasetRow>[]>(
        () => [
            {
                accessorKey: 'displayTitle',
                header: 'Case',
                cell: ({ row }) => (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
                        <OverflowTitle title={row.original.displayTitle} variant="case-table" />
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {row.original.activeJob && (
                                <ProcessingPill>
                                    <RefreshCw size={12} aria-hidden />
                                    {row.original.activeJob.phase ?? 'Processing'}
                                </ProcessingPill>
                            )}
                            {row.original.warningsCount > 0 && (
                                <WarningPill>
                                    <AlertTriangle size={14} aria-hidden />
                                    {row.original.warningsCount} warning
                                    {row.original.warningsCount === 1 ? '' : 's'}
                                </WarningPill>
                            )}
                        </div>
                    </div>
                ),
            },
            {
                accessorKey: 'documentCount',
                header: 'Documents',
                meta: { align: 'right' as const, numeric: true },
                cell: ({ getValue }) => formatNumber(getValue<number>()),
            },
            {
                accessorKey: 'pageCount',
                header: 'Pages',
                meta: { align: 'right' as const, numeric: true },
                cell: ({ getValue }) => formatNumber(getValue<number>()),
            },
            {
                accessorKey: 'reviewCompletion',
                header: 'Progress',
                meta: { align: 'right' as const },
                cell: ({ getValue }) => {
                    const val = getValue<number>();
                    return (
                        <ProgressCell>
                            <div style={{ flex: 1 }}>
                                <ProgressBar value={val} thin />
                            </div>
                            <ProgressLabel>{val > 0 ? `${(val * 100).toFixed(0)}%` : '—'}</ProgressLabel>
                        </ProgressCell>
                    );
                },
            },
            {
                accessorKey: 'lastModified',
                header: 'Updated',
                meta: { align: 'right' as const, muted: true },
                cell: ({ row }) => formatTimestamp(row.original.lastModified),
            },
            {
                id: 'actions',
                header: '',
                enableSorting: false,
                meta: { align: 'right' as const },
                cell: ({ row }) =>
                    onDeleteRef.current ? (
                        <DeleteButton
                            type="button"
                            aria-label={`Delete ${row.original.displayTitle}`}
                            onClick={event => {
                                event.stopPropagation();
                                onDeleteRef.current?.(row.original.dataset);
                            }}
                            disabled={
                                Boolean(isDeletingRef.current) &&
                                deletingDatasetPathRef.current === row.original.dataset.metadataPath
                            }
                        >
                            <Trash2 size={16} aria-hidden />
                        </DeleteButton>
                    ) : null,
                size: 60,
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps -- columns use refs for callbacks
        [],
    );

    const table = useReactTable({
        data: filteredRows,
        columns,
        state: { sorting },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: {
            pagination: { pageIndex: 0, pageSize: 25 },
        },
        getRowId: row => row.id,
    });

    return (
        <TableWrapper>
            <div style={{ marginBottom: 8 }}>
                <SearchFilterBar searchText={searchQuery} onSearchChange={setSearchQuery} placeholder="Search cases…" />
            </div>
            <StyledTable>
                <Thead>
                    {table.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map(header => {
                                const meta = header.column.columnDef.meta as
                                    | { align?: string; numeric?: boolean }
                                    | undefined;
                                const sorted = header.column.getIsSorted();
                                return (
                                    <Th
                                        key={header.id}
                                        onClick={header.column.getToggleSortingHandler()}
                                        $active={!!sorted}
                                        $align={(meta?.align as 'left' | 'center' | 'right') ?? 'left'}
                                        $sortable={header.column.getCanSort()}
                                    >
                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                        {sorted && (
                                            <SortIndicator>{sorted === 'asc' ? ' ↑' : ' ↓'}</SortIndicator>
                                        )}
                                    </Th>
                                );
                            })}
                        </tr>
                    ))}
                </Thead>
                <Tbody>
                    {table.getRowModel().rows.length === 0 ? (
                        <EmptyRow>
                            <td colSpan={columns.length}>No cases found</td>
                        </EmptyRow>
                    ) : (
                        table.getRowModel().rows.map(row => (
                            <Tr
                                key={row.id}
                                $clickable
                                $active={row.original.id === selectedDatasetPath}
                                onClick={() => onOpen(row.original.dataset)}
                            >
                                {row.getVisibleCells().map(cell => {
                                    const meta = cell.column.columnDef.meta as
                                        | { align?: string; numeric?: boolean; muted?: boolean }
                                        | undefined;
                                    return (
                                        <Td
                                            key={cell.id}
                                            $align={(meta?.align as 'left' | 'center' | 'right') ?? 'left'}
                                            $numeric={meta?.numeric}
                                            $muted={meta?.muted}
                                        >
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </Td>
                                    );
                                })}
                            </Tr>
                        ))
                    )}
                </Tbody>
            </StyledTable>
            {table.getPageCount() > 1 && (
                <TablePagination
                    page={table.getState().pagination.pageIndex}
                    pageSize={table.getState().pagination.pageSize}
                    totalRows={filteredRows.length}
                    onPageChange={page => table.setPageIndex(page)}
                    onPageSizeChange={size => table.setPageSize(size)}
                />
            )}
        </TableWrapper>
    );
};
